'use client'

import * as React from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { CalendarIcon, ChevronDownIcon, ClockIcon } from 'lucide-react'
import { z } from 'zod'

import {
  Button,
  DatePresetPicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
} from '@ui/web/components'
import { DUE_PRESETS, START_PRESETS, typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { AssignModuleSubmitData } from './types'

const triggerClass = cn(
  'ui:flex ui:w-full ui:min-w-0 ui:items-center ui:justify-between ui:gap-1.5 ui:font-normal',
  'ui:border ui:border-outline ui:bg-neutral-white ui:text-neutral-black',
  'ui:px-2.5 ui:py-2 ui:h-12 ui:rounded-lg ui:whitespace-nowrap ui:transition-colors ui:outline-none ui:select-none',
  'ui:focus-visible:border-blue-100 ui:focus-visible:ring-3 ui:focus-visible:ring-blue-20',
  'ui:disabled:cursor-not-allowed ui:disabled:bg-grey-10 ui:disabled:opacity-50',
)

function StartTrigger(open: boolean, label: string | null) {
  return (
    <Button baseButton variant='none' className={triggerClass}>
      <span className='ui:flex ui:flex-1 ui:items-center ui:gap-1.5 ui:truncate ui:text-left ui:text-base'>
        <CalendarIcon className='ui:size-5 ui:shrink-0' />
        <span>{label ?? 'Sekarang'}</span>
      </span>
      <ChevronDownIcon
        className={cn('ui:size-4 ui:shrink-0 ui:text-blue-100 ui:transition-transform', open && 'ui:rotate-180')}
      />
    </Button>
  )
}

function DueTrigger(open: boolean, label: string | null) {
  return (
    <Button baseButton variant='none' className={triggerClass}>
      <span className='ui:flex ui:flex-1 ui:items-center ui:gap-1.5 ui:truncate ui:text-left ui:text-base'>
        <ClockIcon className='ui:size-5 ui:shrink-0' />
        <span>{label ?? 'Kapan saja'}</span>
      </span>
      <ChevronDownIcon
        className={cn('ui:size-4 ui:shrink-0 ui:text-blue-100 ui:transition-transform', open && 'ui:rotate-180')}
      />
    </Button>
  )
}

const scheduleSchema = z
  .object({
    startDate: z.date().nullable(),
    endDate: z.date().nullable(),
  })
  .refine(
    (data) => {
      if (data.endDate === null) return true
      const start = data.startDate ?? new Date()
      return data.endDate > start
    },
    { message: 'Jatuh tempo harus setelah jadwal mulai', path: ['endDate'] },
  )

interface ScheduleAssignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: 'school' | 'web'
  onSchedule: (data: Pick<AssignModuleSubmitData, 'startDate' | 'endDate'>) => void | Promise<void>
  onBack: () => void
  isSubmitting?: boolean
}

export function ScheduleAssignDialog({
  open,
  onOpenChange,
  type,
  onSchedule,
  onBack,
  isSubmitting,
}: ScheduleAssignDialogProps) {
  const childLabel = type === 'school' ? 'murid' : 'anak'

  const defaultValues = React.useMemo(() => ({ startDate: null as Date | null, endDate: null as Date | null }), [])

  const form = useForm({
    defaultValues,
    validators: { onSubmit: scheduleSchema },
    onSubmit: async ({ value }) => {
      await onSchedule({ startDate: value.startDate ?? undefined, endDate: value.endDate })
    },
  })

  const canSubmit = useStore(form.store, (s) => s.canSubmit)
  const startDate = useStore(form.store, (s) => s.values.startDate)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='srd:max-w-lg srd:w-full' showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Tugaskan sesuai jadwal</DialogTitle>
          <DialogDescription>
            Tentukan jendela waktu {childLabel} dapat menyelesaikan semua aktivitas dalam tugas ini.
          </DialogDescription>
        </DialogHeader>

        <div className='srd:flex srd:flex-col srd:gap-4 srd:mt-2'>
          <form.Field name='startDate'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field data-invalid={isInvalid || undefined}>
                  <FieldLabel>Jadwal mulai</FieldLabel>
                  <DatePresetPicker
                    presets={START_PRESETS}
                    value={field.state.value}
                    onChange={(v) => {
                      field.handleChange(v)
                      form.setFieldValue('endDate', null)
                    }}
                    trigger={StartTrigger}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          <form.Field name='endDate'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
              return (
                <Field data-invalid={isInvalid || undefined}>
                  <FieldLabel>Jatuh tempo</FieldLabel>
                  <DatePresetPicker
                    key={startDate?.getTime() ?? 'null'}
                    presets={DUE_PRESETS}
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    trigger={DueTrigger}
                    minDate={startDate ?? undefined}
                  />
                  <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-60')}>
                    Pilih kapan saja agar lebih leluasa, pengaturan jadwal dapat diperbarui nanti.
                  </p>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>
        </div>

        <div className='srd:flex srd:flex-col srd:items-center srd:gap-3 srd:mt-2'>
          <Button disabled={!canSubmit || isSubmitting} loading={isSubmitting} onClick={() => void form.handleSubmit()}>
            Tugaskan sesuai jadwal
          </Button>

          <Button variant='outline' disabled={isSubmitting} onClick={onBack}>
            Kembali atur peserta
          </Button>

          <Button
            variant='ghost'
            className='srd:text-neutral-black'
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            Batalkan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
